import { useDevicesList, watchDeep } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';

import { useToastStore } from '@/stores/toast.js';
import { Nullable } from '@/types/common';
import { IMicChain, IMicEffect, IMicrophone } from '@/types/components';
import getScaledLevel from '@/utils/chat/microphone/getScaledLevel';
import { MIC_MIN_RADIUS, MIC_MAX_RADIUS, BUFFER_SIZE, SAMPLE_RATE } from '@/utils/constants/microphone';
import { getInoutDevices, getBrowserAndDeviceInfo } from '@/utils/device';
import truncateToDecimal from '@/utils/truncateToDecimal';
import useQueue from '@/utils/useQueue';

export const useMicrophonesStore = defineStore('microphones', () => {
  const toastStore = useToastStore();
  const { t } = useI18n();

  // 마이크 장치
  const microphones = ref<IMicrophone[]>([]);
  const selectedMicrophoneIds = ref<string[]>([]);
  const micSensitivityById = ref<Record<string, number>>({});
  const micLevelById = ref<Record<string, number>>({});
  const micEventById = ref<Record<string, (data: Float32Array) => void>>({});
  const micEffectById = ref<Record<string, IMicEffect>>({});
  const mixGain = ref<Nullable<GainNode>>(null);

  // 마이크 노드
  const micContext = ref<Nullable<AudioContext>>(null);
  const micChains = ref<Map<string, IMicChain>>(new Map());
  const updateDeviceIntervalId = ref(); // 마이크 장치 인터벌

  // 마이크 업데이트 큐
  const micGraphRenderQueue = useQueue();

  // 마이크 그래프 큐
  const micListUpdateQueue = useQueue();

  const computedConstraintsAudio = computed(() => {
    const { os } = getBrowserAndDeviceInfo();
    let noise = false;
    if (os.toUpperCase() === 'IOS') noise = true;
    else if (os.toUpperCase() === 'ANDROID') noise = false;
    return {
      echoCancellation: false,
      noiseSuppression: noise,
      autoGainControl: false,
      channelCount: 1,
    };
  });

  const { audioInputs, ensurePermissions } = useDevicesList({
    requestPermissions: true,
    constraints: {
      video: false,
      audio: computedConstraintsAudio.value,
    },
  });

  const initMicrophonesStore = async () => {
    if (updateDeviceIntervalId.value) {
      clearInterval(updateDeviceIntervalId.value);
    }
    await updateDevicesAutomatically();
    await stopMics(selectedMicrophoneIds.value);
  };

  const resetMicrophoneStore = async () => {
    if (updateDeviceIntervalId.value) {
      clearInterval(updateDeviceIntervalId.value);
    }
    await stopMics(selectedMicrophoneIds.value);
  };

  const isMicOn = (deviceId: string): boolean => {
    return !!micChains.value.get(deviceId)?.isOn;
  };

  const ensureAudioGraph = async () => {
    if (micContext.value && micContext.value.state !== 'closed') {
      return;
    }

    micContext.value = new AudioContext({ sampleRate: SAMPLE_RATE });
    const base = import.meta.env.BASE_URL;

    const modulePath = base === '/' ? '/js/processor.js' : `${base.endsWith('/') ? base : base + '/'}js/processor.js`;
    await micContext.value.audioWorklet.addModule(modulePath);
    mixGain.value = micContext.value.createGain();
  };

  const buildMicGraph = async (deviceId: string): Promise<IMicChain> => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        ...computedConstraintsAudio.value,
        deviceId: { exact: deviceId },
      },
    });
    const ctx = micContext.value!;
    const source = ctx.createMediaStreamSource(stream);
    const gain = ctx.createGain();
    const analyser = ctx.createAnalyser();

    analyser.fftSize = 1024;

    const processor = new AudioWorkletNode(ctx, 'audio-processor', {
      processorOptions: { bufferSize: BUFFER_SIZE },
    });

    source.connect(gain).connect(analyser).connect(processor);

    processor.port.onmessage = (e: MessageEvent<Float32Array>) => {
      const data = e.data;
      micLevelById.value[deviceId] = getScaledLevel(analyser);
      micEventById.value[deviceId]?.(data);
      micChains.value.get(deviceId)?.socketSender?.(data);
    };
    processor.port.onmessageerror = async () => {
      toastStore.warn(t('unstable_mic'));
      await destroyMicGraph(deviceId);
    };

    return { stream, source, gain, analyser, processor, isOn: true };
  };

  const controlGainById = (deviceId: string) => {
    const c = micChains.value.get(deviceId);
    if (!c) return;

    const { label, initialGain = 15 } = microphones.value.find(({ id }) => id === deviceId) || { initialGain: 15 };
    const sensitivity = micSensitivityById.value[deviceId] ?? 5;

    c.gain.gain.value = parseFloat(
      truncateToDecimal((0.4 + 0.12 * Math.pow(sensitivity - 1, 2) + 0.65 * (sensitivity - 1)) * initialGain).toString()
    );
    console.log(`[${label}] 의 게인`, c.gain.gain.value, sensitivity);
  };
  const destroyMicGraph = async (deviceId: string) => {
    const c = micChains.value.get(deviceId);
    if (!c) return;
    try {
      c.source?.disconnect();
      c.gain?.disconnect();
      c.analyser?.disconnect();
      c.processor?.disconnect();
      c.processor?.port.close();
      c.stream?.getTracks().forEach((t) => t.stop());

      if (micEffectById.value[deviceId]) {
        cancelAnimationFrame(micEffectById.value[deviceId].animationId);
        micEffectById.value[deviceId].animationId = null;
        micEffectById.value[deviceId].isShowMicEffect = false;
      }
    } finally {
      delete micLevelById.value[deviceId];
      delete micEventById.value[deviceId];
      delete micEffectById.value[deviceId];
      micChains.value.delete(deviceId);
    }
  };

  const startMics = async (deviceIds: string[]) => {
    micGraphRenderQueue.add(async () => {
      await ensureAudioGraph();
      for (const id of deviceIds) {
        if (micChains.value.has(id)) continue;
        micChains.value.set(id, await buildMicGraph(id));
        selectedMicrophoneIds.value.push(id);
        controlGainById(id);
        if (!micEffectById.value[id]) {
          setupMicEffect(id);
        }
      }
      await micContext.value?.resume();
    });
  };

  const stopMics = async (deviceIds: string[]) => {
    micGraphRenderQueue.add(async () => {
      for (const id of deviceIds) {
        await destroyMicGraph(id);
      }

      selectedMicrophoneIds.value = selectedMicrophoneIds.value.filter((id) => !deviceIds.includes(id));

      if (micContext.value && selectedMicrophoneIds.value.length <= 0) {
        await micContext.value?.close();
        micContext.value = null;
      }
    });
  };

  const setMicEventById = (deviceId: string, h: (d: Float32Array) => void) => {
    micEventById.value[deviceId] = h;
  };

  // 마이크
  const setupMicEffect = (deviceId: string) => {
    micEffectById.value[deviceId] = {};
    const draw = () => {
      micEffectById.value[deviceId].animationId = requestAnimationFrame(draw);
      micEffectById.value[deviceId].isShowMicEffect = true;
      micEffectById.value[deviceId].outerRadius = Math.min(
        Math.max(micLevelById.value[deviceId] || MIC_MIN_RADIUS, MIC_MIN_RADIUS),
        MIC_MAX_RADIUS
      );
    };
    draw();
  };

  /**
   * @desc 마이크 리스트 업데이트 및 장치변경
   **/
  const updateMicrophones = async (newVal: MediaDeviceInfo[], oldVal: MediaDeviceInfo[]) => {
    const updateMicrophonesWithQueue = async () => {
      const { input = [] } = await getInoutDevices(oldVal, newVal);
      microphones.value = [...cloneDeep(input), ...microphones.value.map((mic) => ({ ...mic, isConnected: false }))]
        .reduce((acc, mic) => {
          const inx = acc.findIndex(({ id }) => mic.id === id);
          if (inx === -1) {
            acc.push({ ...mic });
          } else {
            acc[inx] = {
              ...acc[inx],
              customLabel: mic.customLabel,
            };
          }
          // 마이크 감도 조절
          if (!micSensitivityById.value[mic.id]) {
            micSensitivityById.value[mic.id] = 7;
          }
          return acc;
        }, [])
        .sort(({ isConnected }) => isConnected);
    };
    micListUpdateQueue.add(updateMicrophonesWithQueue);
  };

  /**
   * @desc 3초마다 장치 리스트를 불러옵니다.
   **/
  const updateDevicesAutomatically = async () => {
    updateDeviceIntervalId.value = setInterval(async () => {
      if (updateDeviceIntervalId.value) {
        clearInterval(updateDeviceIntervalId.value);
      }
      const newAudioInputs = (await navigator.mediaDevices.enumerateDevices()).filter((i) => i.kind === 'audioinput');

      await updateMicrophones(cloneDeep(newAudioInputs), cloneDeep(audioInputs.value));
    }, 3000);
  };

  /**
   * @desc audioInputs가 변경될 때 마이크 장치 목록을 업데이트합니다.
   **/
  watchDeep(audioInputs, async (newInputs, oldInputs) => {
    await updateMicrophones(newInputs, oldInputs);
  });

  return {
    isMicOn,
    micLevelById,
    micSensitivityById,
    micEffectById,
    microphones,
    selectedMicrophoneIds,
    initMicrophonesStore,
    resetMicrophoneStore,
    startMics,
    stopMics,
    setMicEventById,
    controlGainById,
    ensurePermissions,
  };
});
